<template>
  <CommonLayout>
    <!-- 新增卡片 -->
    <el-card style="margin-top: 20px;">
      <template #header>
        <div class="card-header">
          <el-icon><Document /></el-icon>
          <span>临时密钥列表</span>
          <!-- 新增刷新图标 -->
          <el-icon @click="refreshTemporaryKeys" :class="{ 'refresh-icon': true, 'loading': refreshing }">
            <template v-if="refreshing">
              <Loading />
            </template>
            <template v-else>
              <Refresh />
            </template>
          </el-icon>
        </div>
      </template>
      <div class="card-content">
        <!-- 新增按钮 -->
        <div class="header" style="padding-left: 20px;">
          <el-tooltip class="item" effect="dark" content="一个用户最多生成 2 条记录" placement="right">
            <el-button 
              @click="generateTmpKey" 
              type="primary"
              :disabled="temporaryKeys.length >= 2"
            >
              新增
            </el-button>
          </el-tooltip>
        </div>
        <!-- 表格 -->
        <div class="content">
          <div style="overflow-x: auto;overflow-y: hidden;">
            <el-table :data="temporaryKeys" style="width: 100%;">
              <template #empty>
                <div style="text-align: center;">暂无数据</div>
              </template>
              <el-table-column prop="key" label="临时密钥(tmpToken)" min-width="286" />
              <el-table-column prop="password" label="临时密码(secretKey)" min-width="286" />
              <el-table-column prop="expiry" label="过期时间" min-width="120" />
              <el-table-column prop="createTime" label="创建时间" min-width="180" />
              <el-table-column prop="updateTime" label="最近使用时间" min-width="180" >
                <template #default="scope">
                  {{ scope.row.updateTime == scope.row.createTime ? '未使用' : scope.row.updateTime }}
                </template>
              </el-table-column>
              <el-table-column label="操作" fixed="right">
                <template #default="scope">
                  <el-icon @click="showExpireDialog(scope.row.key, scope.row.expiry)" style="cursor: pointer; color: #409EFF; margin-right: 10px;">
                    <Edit />
                  </el-icon>
                  <el-icon @click="handleDelete(scope.row.key)" style="cursor: pointer; color: red;">
                    <Delete />
                  </el-icon>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>
    </el-card>
    <!-- 新增提示文案 -->
    <div class="data-update-tip">提示：建议每半年更换一次临时密钥，避免泄露。</div>
    <el-dialog v-model="expireDialogVisible" title="设置过期时间" width="35%">
      <el-form :model="expireForm">
        <el-form-item label="临时密钥">
          <span>{{ expireForm.tmpToken }}</span>
        </el-form-item>
        <el-form-item label="过期时间">
          <el-date-picker
            v-model="expireForm.expireDate"
            type="date"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            placeholder="选择过期时间"
          >
          </el-date-picker>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="expireDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="setExpireDate">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </CommonLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { generateToken } from '@/utils/crypto'
import { API_PATHS } from '@/config/api'
import CommonLayout from '@/components/CommonLayout.vue'
import { ElMessageBox, ElMessage } from 'element-plus'
import { useRouter } from 'vue-router' 
import service from '@/utils/http';
import { Edit, Delete, Refresh, Loading } from '@element-plus/icons-vue' // 引入刷新和加载图标

const router = useRouter() 

const temporaryKeys = ref([])
const loading = ref(false)
const refreshing = ref(false) // 新增刷新状态变量

const getUserId = () => {
  const userInfo = localStorage.getItem('userInfo')
  if (!userInfo) {
    router.push('/auth')
    return null
  }
  const parsedUserInfo = JSON.parse(userInfo)
  return parsedUserInfo.userId
}

const handleDelete = async (key) => {
  try {
    await ElMessageBox.confirm(
      `是否确认删除 ${key}？删除后将立即无法使用！`,
      '提示',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    loading.value = true
    const userId = getUserId()
    if (!userId) return
    const timestamp = new Date().getTime().toString();
    const token = generateToken({ userId, tmpToken: key, timestamp });
    const params = {
      userId,
      tmpToken: key,
      timestamp,
      token
    };
    const formData = new URLSearchParams();
    for (const paramKey in params) {
      formData.append(paramKey, params[paramKey]);
    }
    const response = await service.post(`${API_PATHS.deleteTmpKey}`, formData);
    ElMessage.success(response.data);
    await getTemporaryKeys();
  } catch (error) {
    if (error === 'cancel') {
      return;
    }
    ElMessage.error(error.message || '删除临时密钥失败');
  } finally {
    loading.value = false;
  }
}

const getTemporaryKeys = async () => {
  loading.value = true
  try {
    const userId = getUserId()
    if (!userId) return
    const timestamp = new Date().getTime().toString();
    const token = generateToken({ userId, timestamp });
    const params = {
      userId,
      timestamp,
      token
    };
    const response = await service.get(`${API_PATHS.tmpKeyList}`, {
      params,
    });
    temporaryKeys.value = response.data.map(item => ({
      key: item.tmpToken,
      password: item.secretKey,
      expiry: item.expireDate,
      createTime: item.createTime,
      updateTime: item.updateTime
    }));
  } catch (error) {
    ElMessage.error(error.message || '获取临时密钥列表失败');
  } finally {
    loading.value = false; 
    refreshing.value = false; // 刷新完成，重置刷新状态
  }
}

const generateTmpKey = async () => {
  loading.value = true
  try {
    const userId = getUserId()
    if (!userId) return
    const timestamp = new Date().getTime().toString();
    const token = generateToken({ userId, timestamp });
    const params = {
      userId,
      timestamp,
      token
    };
    const formData = new URLSearchParams();
    for (const key in params) {
      formData.append(key, params[key]);
    }
    await service.post(`${API_PATHS.generateTmpKey}`, formData);
    await getTemporaryKeys();
  } catch (error) {
    ElMessage.error(error.message || '生成临时密钥失败');
  } finally {
    loading.value = false; 
  }
}

const expireDialogVisible = ref(false)
const expireForm = ref({
  userId: null,
  tmpToken: '',
  expireDate: '',
  timestamp: '',
  token: ''
})

const showExpireDialog = (key, expiry) => {
  expireForm.value.tmpToken = key
  expireForm.value.expireDate = expiry
  expireDialogVisible.value = true
}

const setExpireDate = async () => {
  try {
    const userId = getUserId()
    if (!userId) return
    const timestamp = new Date().getTime().toString()
    const token = generateToken({ userId, tmpToken: expireForm.value.tmpToken, expireDate: expireForm.value.expireDate, timestamp })
    expireForm.value.userId = userId
    expireForm.value.timestamp = timestamp
    expireForm.value.token = token

    const formData = new URLSearchParams()
    for (const key in expireForm.value) {
      formData.append(key, expireForm.value[key])
    }

    const response = await service.post(`${API_PATHS.expireTmpKey}`, formData)
    ElMessage.success(response.data)
    expireDialogVisible.value = false
    await getTemporaryKeys()
  } catch (error) {
    ElMessage.error(error.message || '设置过期时间失败')
  }
}

// 新增刷新方法
const refreshTemporaryKeys = () => {
  refreshing.value = true; // 开始刷新，设置刷新状态
  getTemporaryKeys();
}

onMounted(() => {
  getTemporaryKeys()
})
</script>

<style scoped>
/* 页面特定样式 */
.header {
  margin-bottom: 20px;
}

.content {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
}

/* 去掉表格边框 */
.el-table {
  border: none; 
}

.el-table th,
.el-table td {
  border: none; 
}

.el-table::before {
  display: none; 
}

.el-dialog {
  width: 35% !important;
}

/* 新增样式，参照 OperationData.vue 中的提示文案样式 */
.data-update-tip {
  color: red; 
  font-size: 12px; 
  margin-top: 10px; 
}

/* 新增卡片样式，参照 OperationData.vue 中的操作记录卡片 */
.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
}

.card-header .el-icon {
  font-size: 20px;
  color: #409EFF;
}

.card-content {
  padding: 20px;
  background-color: #fff; 
  border-radius: 4px; 
}

/* 刷新图标样式 */
.refresh-icon {
  margin-left: auto; 
  cursor: pointer; 
  color: #999; 
  transition: color 0.3s; 
}

.refresh-icon.loading {
  animation: spin 1s linear infinite; 
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.refresh-icon:hover {
  color: #409EFF; 
}
</style>